import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import {
  addComment,
  deleteComment,
  editComment,
  hideComment,
  listComments,
  reportComment,
  unhideComment,
  type Comment,
} from '../../api/community';
import { listReportKinds, type ReportKind } from '../../api/reports';
import type { Post } from '../../api/posts';
import { useAuth } from '../../auth/useAuth';
import { Alert, Button, Card, EmptyState, LoadingState } from '../ui';
import './StoryComments.css';

/**
 * The conversation under a story (Phase 18).
 *
 * **Flat, deliberately** — a list of standalone things said about one story,
 * not a forum. Oldest first, because a conversation read newest-first is one
 * nobody can follow.
 *
 * Three people can be reading this, and each is shown something different:
 *
 * * whoever wrote a comment may change it or take it back;
 * * anybody else may report one, and never sees a hidden one at all;
 * * a moderator may take one out of view, with a reason its author reads.
 *
 * The story's author is not offered the box: you cannot comment on your own
 * work, and the backend refuses it — this only avoids offering what would be
 * refused (Rule 7).
 */
export function StoryComments({ post }: { post: Post }) {
  const { account } = useAuth();
  const canModerate = account?.role === 'MODERATOR' || account?.role === 'ADMIN';

  const [comments, setComments] = useState<Comment[] | null>(null);
  /**
   * How many *this reader* can see, which is not always how many there are: a
   * hidden comment stays visible to its own author and to nobody else. Counted
   * from the same query that produced the list, so the heading can never
   * disagree with what is underneath it — a live run caught it reading
   * "0 comments" directly above a comment.
   */
  const [total, setTotal] = useState<number | null>(null);
  /**
   * How many pages have been read. A conversation is read forward, so "more"
   * appends rather than replaces — and a reload after an edit or a hide re-reads
   * every page already open, so nothing the reader had scrolled past disappears
   * underneath them.
   */
  const [pages, setPages] = useState(1);
  const [more, setMore] = useState(false);
  const [body, setBody] = useState('');
  const [working, setWorking] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(
    async (through = pages) => {
      try {
        const read = [];
        let last = null;
        for (let number = 1; number <= through; number += 1) {
          last = await listComments(post.id, number);
          read.push(...last.results);
        }
        setComments(read);
        setTotal(last?.count ?? 0);
        setMore(Boolean(last?.next));
        setPages(through);
      } catch {
        setComments([]);
        setTotal(0);
        setMore(false);
      }
    },
    [post.id, pages],
  );

  // Only when the story changes: `load` closes over `pages`, and re-running it
  // on every page read would be a loop.
  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id]);

  const run = async (marker: string, action: () => Promise<unknown>) => {
    setWorking(marker);
    setError(null);
    try {
      await action();
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setWorking(null);
    }
  };

  /**
   * Its own handler rather than going through `run`, which reloads afterwards:
   * that reload would have re-read only the pages already open and put the
   * reader straight back where they started. Caught by the test below.
   */
  const readMore = async () => {
    setWorking('more');
    setError(null);
    try {
      await load(pages + 1);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setWorking(null);
    }
  };

  const say = () =>
    run('new', async () => {
      await addComment(post.id, body);
      setBody('');
    });

  // The story's own count until the list has answered, so the heading is right
  // from the first paint rather than starting at zero and correcting itself.
  const shown = total ?? post.comment_count;

  return (
    <section className="sc-comments" aria-labelledby="sc-comments-title">
      <h2 className="sc-comments__title" id="sc-comments-title">
        {shown === 1 ? '1 comment' : `${shown} comments`}
      </h2>

      {error && (
        <Alert tone="warning" title="That did not work">
          {error.message}
        </Alert>
      )}

      {comments === null ? (
        <LoadingState label="Reading the conversation…" />
      ) : comments.length === 0 ? (
        <EmptyState
          title="Nothing said yet"
          description={
            post.is_mine
              ? 'When somebody comments on this story, it will appear here.'
              : 'Be the first to say something.'
          }
        />
      ) : (
        <ol className="sc-comments__list">
          {comments.map((comment) => (
            <li key={comment.id}>
              <CommentRow
                comment={comment}
                postId={post.id}
                canModerate={canModerate}
                busy={working === comment.id}
                onRun={(action) => run(comment.id, action)}
              />
            </li>
          ))}
        </ol>
      )}

      {more && (
        <p className="sc-comments__more">
          <Button
            size="sm"
            variant="secondary"
            isLoading={working === 'more'}
            onClick={() => void readMore()}
          >
            Read more comments
          </Button>
        </p>
      )}

      {account && !post.is_mine && (
        <div className="sc-comments__write">
          <label className="sc-comments__field">
            <span>Say something about this story</span>
            <textarea
              rows={3}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="The second chart is the one that changed my mind."
            />
          </label>
          <Button size="sm" isLoading={working === 'new'} disabled={!body.trim()} onClick={say}>
            Post comment
          </Button>
        </div>
      )}

      {account && post.is_mine && (
        <p className="sc-comments__muted">
          This is your story. Another paragraph says more than a comment would.
        </p>
      )}
    </section>
  );
}

function CommentRow({
  comment,
  postId,
  canModerate,
  busy,
  onRun,
}: {
  comment: Comment;
  postId: string;
  canModerate: boolean;
  busy: boolean;
  onRun: (action: () => Promise<unknown>) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [reporting, setReporting] = useState(false);
  const [hiding, setHiding] = useState(false);

  const who =
    comment.author.display_name === comment.author.handle
      ? `@${comment.author.handle}`
      : `${comment.author.display_name} · @${comment.author.handle}`;

  return (
    <article className={`sc-comment${comment.is_hidden ? ' sc-comment--hidden' : ''}`}>
      <header className="sc-comment__who">
        <span>{who}</span>
        <time dateTime={comment.created_at}>
          {new Date(comment.created_at).toLocaleDateString()}
        </time>
        {comment.edited_at && <span className="sc-comment__edited">edited</span>}
      </header>

      {/* Only its own author is ever handed a hidden comment, and they are told
          why rather than left to wonder where it went. */}
      {comment.is_hidden && (
        <Alert tone="warning" title="A moderator took this out of view">
          {comment.hidden_reason || 'Only you can see it.'}
        </Alert>
      )}

      {editing ? (
        <div className="sc-comment__write">
          <textarea
            rows={3}
            aria-label="Change what you said"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <div className="sc-comment__actions">
            <Button
              size="sm"
              isLoading={busy}
              onClick={() =>
                onRun(async () => {
                  await editComment(postId, comment.id, draft);
                  setEditing(false);
                })
              }
            >
              Save
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
              Never mind
            </Button>
          </div>
        </div>
      ) : (
        <p className="sc-comment__body">{comment.body}</p>
      )}

      {!editing && (
        <div className="sc-comment__actions">
          {comment.is_mine && !comment.is_hidden && (
            <button type="button" className="sc-comment__link" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
          {comment.is_mine && (
            <button
              type="button"
              className="sc-comment__link"
              onClick={() => onRun(() => deleteComment(postId, comment.id))}
            >
              Delete
            </button>
          )}
          {!comment.is_mine && (
            <button type="button" className="sc-comment__link" onClick={() => setReporting(true)}>
              Report
            </button>
          )}
          {canModerate && !comment.is_hidden && (
            <button type="button" className="sc-comment__link" onClick={() => setHiding(true)}>
              Take out of view
            </button>
          )}
          {canModerate && comment.is_hidden && (
            <button
              type="button"
              className="sc-comment__link"
              onClick={() => onRun(() => unhideComment(comment.id))}
            >
              Put it back
            </button>
          )}
        </div>
      )}

      {reporting && (
        <ReportComment
          postId={postId}
          commentId={comment.id}
          busy={busy}
          onRun={onRun}
          onClose={() => setReporting(false)}
        />
      )}

      {hiding && (
        <HideComment
          commentId={comment.id}
          busy={busy}
          onRun={onRun}
          onClose={() => setHiding(false)}
        />
      )}
    </article>
  );
}

function ReportComment({
  postId,
  commentId,
  busy,
  onRun,
  onClose,
}: {
  postId: string;
  commentId: string;
  busy: boolean;
  onRun: (action: () => Promise<unknown>) => void;
  onClose: () => void;
}) {
  const [kinds, setKinds] = useState<ReportKind[]>([]);
  const [kind, setKind] = useState('');
  const [details, setDetails] = useState('');

  useEffect(() => {
    listReportKinds()
      .then((found) => {
        setKinds(found);
        setKind(found[0]?.value ?? '');
      })
      .catch(() => setKinds([]));
  }, []);

  return (
    <Card>
      <p className="sc-comments__muted">
        This does not take the comment down. A moderator reads what you write and decides.
      </p>
      <label className="sc-comments__field">
        <span>What is wrong with it</span>
        <select value={kind} onChange={(event) => setKind(event.target.value)}>
          {kinds.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </select>
      </label>
      <label className="sc-comments__field">
        <span>What a moderator should check</span>
        <textarea
          rows={3}
          value={details}
          onChange={(event) => setDetails(event.target.value)}
          placeholder="This is directed at the author personally."
        />
      </label>
      <div className="sc-comment__actions">
        <Button
          size="sm"
          isLoading={busy}
          onClick={() =>
            onRun(async () => {
              await reportComment(postId, commentId, kind, details);
              onClose();
            })
          }
        >
          Send it
        </Button>
        <Button size="sm" variant="secondary" onClick={onClose}>
          Never mind
        </Button>
      </div>
    </Card>
  );
}

function HideComment({
  commentId,
  busy,
  onRun,
  onClose,
}: {
  commentId: string;
  busy: boolean;
  onRun: (action: () => Promise<unknown>) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');

  return (
    <Card>
      <label className="sc-comments__field">
        <span>Why it is being taken out of view</span>
        <textarea
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Directed at the author personally rather than at the work."
        />
      </label>
      {/* Said plainly, because it is the part a moderator most needs to know. */}
      <p className="sc-comments__muted">Its author will read this. Nobody else sees it.</p>
      <div className="sc-comment__actions">
        <Button
          size="sm"
          isLoading={busy}
          disabled={!reason.trim()}
          onClick={() =>
            onRun(async () => {
              await hideComment(commentId, reason);
              onClose();
            })
          }
        >
          Take it out of view
        </Button>
        <Button size="sm" variant="secondary" onClick={onClose}>
          Never mind
        </Button>
      </div>
    </Card>
  );
}
